import { type Table as TanstackTable, flexRender } from '@tanstack/react-table';
import { cn } from '@r/utils/utils.ts';

import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from '@r/components/ui/table.tsx';
import { useNavigate } from '@tanstack/react-router';
import { DataTablePagination } from './data-table-pagination.tsx';
import { ListIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import type { ReactNode, ElementType } from 'react';

interface DataTableProps<TData> {
  className?: string;
  /**
   * The table instance returned from useDataTable hook with pagination, sorting, filtering, etc.
   * //@type TanstackTable<TData>
   */
  table: TanstackTable<TData>;

  /**
   * The floating bar to render at the bottom of the table on row selection.
   * @default null
   * @type ReactNode | null
   * @example floatingBar={<TasksTableFloatingBar table={table} />}
   */
  floatingBar?: ReactNode | null;

  /**
   * Whether to render the pagination component.
   * @default true
   * @type boolean
   */
  pagination?: boolean;

  /**
   * The function to set the dialog order.
   * @default null
   * @type (order: object) => void
   */
  onRowClick?: (row: TData) => void;
  getRowClassName?: (row: TData) => string | undefined;

  /**
   * The icon to render when the table is empty.
   * @default ListIcon
   * @type ElementType
   */
  emptyIcon?: ElementType;

  /**
   * The message to render when the table is empty.
   * @default 'Aucune donnée disponible pour le moment.'
   * @type string
   */
  emptyMessageKey?: string;

  /**
   * The message to render when the table is empty after filtering.
   * @default 'Aucun élément ne correspond aux filtres appliqués.'
   * @type string
   */
  emptyFilteredMessageKey?: string;

  /**
   * Total number of items matching the current query (filters included), from the
   * server pagination response (`pagination.totalItems`). Displayed as a results count.
   * @type number
   */
  totalItems?: number;

  /**
   * Page sizes offered by the pagination selector (e.g. to respect an API `perPage` cap).
   * @default [10, 20, 30, 40, 50]
   * @type number[]
   */
  pageSizeOptions?: number[];
}

export function DataTable<TData extends object>({
  table,
  floatingBar = null,
  pagination = true,
  onRowClick,
  getRowClassName,
  emptyIcon = ListIcon,
  emptyMessageKey,
  emptyFilteredMessageKey,
  totalItems,
  pageSizeOptions,
  className
}: DataTableProps<TData>) {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const TableEmptyMessage = ({ isFiltered = false }) => {
    const Icon = emptyIcon;
    const translatedEmptyMessage = t(emptyMessageKey || 'data.table.empty');
    const translatedEmptyFilteredMessage = t(
      emptyFilteredMessageKey || 'data.table.empty.filtered'
    );

    return (
      <div className="flex flex-col justify-center items-center py-10 space-y-2 text-muted-foreground grow">
        <Icon className="w-12 h-12 text-muted-foreground" />

        <p className="text-base font-medium text-center">
          {isFiltered ? translatedEmptyFilteredMessage : translatedEmptyMessage}
        </p>
      </div>
    );
  };

  return (
    <div className="block">
      <div className={cn('overflow-hidden rounded-3xl border shadow-sm', className)}>
        <Table className="w-full">
          <TableHeader>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => {
                  return (
                    <TableHead key={header.id}>
                      {header.isPlaceholder
                        ? null
                        : flexRender(header.column.columnDef.header, header.getContext())}
                    </TableHead>
                  );
                })}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody className="bg-background">
            {table.getRowModel().rows?.length ? (
              table.getRowModel().rows.map((row) => {
                return (
                  <TableRow
                    key={row.id}
                    data-state={row.getIsSelected() && 'selected'}
                    className={getRowClassName?.(row.original)}
                  >
                    {row.getVisibleCells().map((cell) => {
                      const originalRow = row.original as TData & { url?: string };

                      return (
                        <TableCell
                          className={originalRow.url || onRowClick ? 'cursor-pointer' : ''}
                          key={cell.id}
                          onClick={() => {
                            if ('url' in originalRow && originalRow.url) {
                              navigate({ to: originalRow.url });
                            } else if (onRowClick) {
                              onRowClick(originalRow);
                            }
                          }}
                        >
                          {flexRender(cell.column.columnDef.cell, cell.getContext())}
                        </TableCell>
                      );
                    })}
                  </TableRow>
                );
              })
            ) : (
              <TableRow>
                <TableCell colSpan={table.getAllColumns().length} className="h-24 text-center">
                  <TableEmptyMessage isFiltered={table.getState().columnFilters.length > 0} />
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      {pagination && (
        <div className="flex flex-col gap-2.5">
          <DataTablePagination
            table={table}
            totalItems={totalItems}
            pageSizeOptions={pageSizeOptions}
          />
          {table.getFilteredSelectedRowModel().rows.length > 0 && floatingBar}
        </div>
      )}
    </div>
  );
}
